import React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { LIST_EVENTS_HOME_STATISTICS_RESOURCE } from 'src/features/home/api/useListEventHomeStatistics';
import { actionMessages } from 'src/features/navigation/utils/messages';
import { LIST_ACTIVE_STAYS_RESOURCE } from 'src/features/stay/api/useListActiveStays';
import { useCreateVisitor } from 'src/features/visitor/api/useCreateVIsitor';
import VisitorForm from 'src/features/visitor/components/form/VisitorForm';
import { VisitorFormSchemaValues, visitorFormSchema } from 'src/features/visitor/utils/formSchema';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHead,
    DialogHeader,
    DialogPortal,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};
const CreateVisitorModalForm = ({ open, onOpenChange }: Props) => {
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const form = useForm<VisitorFormSchemaValues>({
        resolver: zodResolver(visitorFormSchema),
        defaultValues: {},
    });

    React.useEffect(() => {
        form.reset({});
    }, [form]);

    const onError = useOnError();

    const setIsModalOpen = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        onOpenChange(isOpen);
    };

    const { mutate, isPending } = useCreateVisitor({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACTIVE_STAYS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_EVENTS_HOME_STATISTICS_RESOURCE] });
            toast({
                title: formatMessage(actionMessages.addVisitorSuccess),
                description: formatMessage(actionMessages.addVisitorSuccessDescription, {
                    entry: success?.identifier_value,
                }),
            });
            setIsModalOpen(false);
        },
        onError: (error) => onError(error, form.setError),
    });

    const onSubmit = (values: VisitorFormSchemaValues) => mutate({ parkingId, ...values });

    return (
        <Dialog open={open} onOpenChange={setIsModalOpen} modal>
            <DialogPortal>
                <DialogContent>
                    <DialogHeader className="flex-row gap-4 space-y-0">
                        <SquareIcon icon={ICON_MAP.ADD} className="shrink-0" />
                        <DialogHead className="space-y-0">
                            <DialogTitle>{formatMessage(actionMessages.addVisitor)}</DialogTitle>
                            <DialogDescription>{formatMessage(actionMessages.addVisitorToParking)}</DialogDescription>
                        </DialogHead>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <VisitorForm form={form} onSubmit={onSubmit} />
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                {formatMessage(generalMessages.cancel)}
                            </Button>
                        </DialogClose>
                        <Button
                            disabled={isPending || !form.formState.isDirty}
                            type="submit"
                            onClick={form.handleSubmit(onSubmit)}
                            className="flex-1"
                        >
                            {isPending && (
                                <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                            )}
                            {formatMessage(actionMessages.addVisitor)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
};

export default CreateVisitorModalForm;
